import { msg, str } from '@lit/localize';
import type { WorkflowVersionEvent } from '../../../api/types';
import { formatDate, formatDateTime } from '../../../utils/format';

export type VersionCategory =
  | 'workflow'
  | 'task'
  | 'annotation'
  | 'connection'
  | 'webhook'
  | 'task_group';
export type VersionFilter = 'all' | VersionCategory;
export type VersionChange = { from?: unknown; to?: unknown };
export type VersionDiffLine = {
  type: 'context' | 'added' | 'removed';
  text: string;
};

const MAX_LINE_DIFF_CELLS = 40000;
const MAX_FALLBACK_LINES_PER_SIDE = 80;
const MAX_LINE_DIFF_CACHE_ENTRIES = 50;
const lineDiffCache = new Map<string, VersionDiffLine[]>();

export const VERSION_FILTERS: VersionFilter[] = [
  'all',
  'workflow',
  'task',
  'task_group',
  'annotation',
  'connection',
  'webhook',
];

export const VERSION_GROUPS: WorkflowVersionEvent['group'][] = [
  'Today',
  'Yesterday',
  'Earlier',
];

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function isVersionChange(value: unknown): value is VersionChange {
  return isRecord(value) && ('from' in value || 'to' in value);
}

export function getVersionChanges(
  event: WorkflowVersionEvent
): Record<string, VersionChange> {
  const changes = event.payload?.changes;
  if (!isRecord(changes)) return {};

  const normalized: Record<string, VersionChange> = {};
  for (const [field, change] of Object.entries(changes)) {
    if (!isVersionChange(change)) continue;
    normalized[field] = {
      from: change.from,
      to: change.to,
    };
  }
  return normalized;
}

export function getVersionCategory(
  event: WorkflowVersionEvent
): VersionCategory {
  if (event.type.startsWith('workflow_')) return 'workflow';
  if (event.type.startsWith('task_group_')) return 'task_group';
  if (event.type.startsWith('task_')) return 'task';
  if (event.type.startsWith('annotation_')) return 'annotation';
  if (event.type.startsWith('webhook_')) return 'webhook';
  return 'connection';
}

export function getVersionCategoryLabel(category: VersionCategory) {
  switch (category) {
    case 'workflow':
      return msg('Workflow');
    case 'task':
      return msg('Task');
    case 'annotation':
      return msg('Annotation');
    case 'connection':
      return msg('Connection');
    case 'webhook':
      return msg('Webhook');
    case 'task_group':
      return msg('Task group');
  }
}

export function formatVersionField(field: string) {
  const withoutPrefix = field.replace(/^config\./, '').replace(/^state\./, '');
  return withoutPrefix
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

export function getVersionActionLabel(event: WorkflowVersionEvent) {
  if (event.type.endsWith('_created') || event.type.endsWith('_added')) {
    return msg('Added');
  }
  if (event.type.endsWith('_deleted') || event.type.endsWith('_removed')) {
    return msg('Removed');
  }
  if (event.type.endsWith('_renamed')) return msg('Renamed');
  return msg('Changed');
}

export function getVersionEntityLabel(event: WorkflowVersionEvent) {
  const payload = event.payload ?? {};
  if (event.type.startsWith('workflow_')) return msg('Workflow');
  if (event.type.startsWith('task_group_')) {
    return msg(str`Task group #${String(payload.taskGroupId ?? '?')}`);
  }
  if (event.type.startsWith('task_')) {
    return msg(str`Task #${String(payload.taskId ?? '?')}`);
  }
  if (event.type.startsWith('annotation_')) {
    return msg(str`Annotation ${String(payload.annotationId ?? '?')}`);
  }
  if (event.type.startsWith('webhook_')) {
    return msg(str`Webhook ${String(payload.webhookId ?? '?')}`);
  }
  return msg(str`Connection #${String(payload.connectionId ?? '?')}`);
}

export function getPrimaryVersionField(event: WorkflowVersionEvent) {
  const firstField = Object.keys(getVersionChanges(event))[0];
  if (firstField) return formatVersionField(firstField);
  return getVersionActionLabel(event);
}

export function getVersionHeading(event: WorkflowVersionEvent) {
  const category = getVersionCategoryLabel(getVersionCategory(event));
  return `${category}: ${getPrimaryVersionField(event)}`;
}

export function getPrimaryVersionChange(event: WorkflowVersionEvent) {
  return Object.values(getVersionChanges(event))[0];
}

export function formatVersionValue(value: unknown) {
  if (value == null || value === '') return msg('None');
  if (typeof value === 'boolean') return value ? msg('Yes') : msg('No');
  if (typeof value === 'object') {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

export function getVersionActorName(event: WorkflowVersionEvent) {
  return (
    event.actorName?.trim() ||
    event.actorEmail?.trim() ||
    (event.actorUserId
      ? msg(str`User ${event.actorUserId}`)
      : msg('Unknown user'))
  );
}

export function getVersionActorInitials(event: WorkflowVersionEvent) {
  const name = getVersionActorName(event);
  const parts = name.replace(/@.*/, '').split(/\s+/).filter(Boolean);
  if (parts.length > 1) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
}

export function getVersionActorTone(event: WorkflowVersionEvent) {
  const seed =
    event.actorUserId ?? event.actorEmail ?? event.actorName ?? 'unknown';
  let hash = 0;
  for (const char of String(seed)) hash += char.charCodeAt(0);
  return hash % 5;
}

export function formatVersionTime(
  event: WorkflowVersionEvent,
  compact = false
) {
  const date = new Date(event.createdAt);
  if (compact && (event.group === 'Today' || event.group === 'Yesterday')) {
    return formatDateTime(date, {
      hour: '2-digit',
      minute: '2-digit',
    });
  }
  if (compact) {
    return formatDate(date, { month: 'short', day: 'numeric' });
  }
  return formatDateTime(date, {
    dateStyle: 'medium',
    timeStyle: 'short',
  });
}

export function getVersionGroupLabel(group: string) {
  if (group === 'Today') return msg('Today');
  if (group === 'Yesterday') return msg('Yesterday');
  if (group === 'Earlier') return msg('Older');
  return group;
}

export function versionMatchesFilter(
  event: WorkflowVersionEvent,
  filter: VersionFilter
) {
  return filter === 'all' || getVersionCategory(event) === filter;
}

export function versionMatchesSearch(
  event: WorkflowVersionEvent,
  search: string
) {
  const query = search.trim().toLowerCase();
  if (!query) return true;
  const changes = getVersionChanges(event);
  const haystack = [
    event.type,
    getVersionEntityLabel(event),
    getVersionHeading(event),
    getVersionActionLabel(event),
    getVersionActorName(event),
    event.actorEmail ?? '',
    ...Object.keys(changes),
    ...Object.values(changes).flatMap((change) => [
      formatVersionValue(change.from),
      formatVersionValue(change.to),
    ]),
  ]
    .join(' ')
    .toLowerCase();
  return haystack.includes(query);
}

export function filterVersionEvents(
  events: WorkflowVersionEvent[],
  filter: VersionFilter,
  search: string
) {
  return events.filter(
    (event) =>
      versionMatchesFilter(event, filter) && versionMatchesSearch(event, search)
  );
}

export function groupVersionEvents(events: WorkflowVersionEvent[]) {
  const grouped = new Map<
    WorkflowVersionEvent['group'],
    WorkflowVersionEvent[]
  >();
  for (const event of events) {
    const list = grouped.get(event.group) ?? [];
    list.push(event);
    grouped.set(event.group, list);
  }
  return grouped;
}

export function createLineDiff(
  before: string,
  after: string
): VersionDiffLine[] {
  const cacheKey = `${before}\u0000${after}`;
  const cached = lineDiffCache.get(cacheKey);
  if (cached) return cached;

  const beforeLines = before.split('\n');
  const afterLines = after.split('\n');
  const matrixCells = (beforeLines.length + 1) * (afterLines.length + 1);
  if (matrixCells > MAX_LINE_DIFF_CELLS) {
    return cacheLineDiff(
      cacheKey,
      createFallbackLineDiff(beforeLines, afterLines)
    );
  }

  const table = Array.from({ length: beforeLines.length + 1 }, () =>
    Array(afterLines.length + 1).fill(0)
  ) as number[][];

  for (let i = beforeLines.length - 1; i >= 0; i -= 1) {
    for (let j = afterLines.length - 1; j >= 0; j -= 1) {
      table[i][j] =
        beforeLines[i] === afterLines[j]
          ? table[i + 1][j + 1] + 1
          : Math.max(table[i + 1][j], table[i][j + 1]);
    }
  }

  const lines: VersionDiffLine[] = [];
  let i = 0;
  let j = 0;
  while (i < beforeLines.length && j < afterLines.length) {
    if (beforeLines[i] === afterLines[j]) {
      lines.push({ type: 'context', text: beforeLines[i] });
      i += 1;
      j += 1;
    } else if (table[i + 1][j] >= table[i][j + 1]) {
      lines.push({ type: 'removed', text: beforeLines[i] });
      i += 1;
    } else {
      lines.push({ type: 'added', text: afterLines[j] });
      j += 1;
    }
  }

  while (i < beforeLines.length) {
    lines.push({ type: 'removed', text: beforeLines[i] });
    i += 1;
  }
  while (j < afterLines.length) {
    lines.push({ type: 'added', text: afterLines[j] });
    j += 1;
  }

  return cacheLineDiff(cacheKey, lines);
}

function cacheLineDiff(key: string, lines: VersionDiffLine[]) {
  lineDiffCache.set(key, lines);
  if (lineDiffCache.size > MAX_LINE_DIFF_CACHE_ENTRIES) {
    const oldestKey = lineDiffCache.keys().next().value;
    if (oldestKey) {
      lineDiffCache.delete(oldestKey);
    }
  }
  return lines;
}

function createFallbackLineDiff(
  beforeLines: string[],
  afterLines: string[]
): VersionDiffLine[] {
  const lines: VersionDiffLine[] = [];
  for (const line of beforeLines.slice(0, MAX_FALLBACK_LINES_PER_SIDE)) {
    lines.push({ type: 'removed', text: line });
  }
  if (beforeLines.length > MAX_FALLBACK_LINES_PER_SIDE) {
    lines.push({
      type: 'context',
      text: `${beforeLines.length - MAX_FALLBACK_LINES_PER_SIDE} removed lines hidden`,
    });
  }
  for (const line of afterLines.slice(0, MAX_FALLBACK_LINES_PER_SIDE)) {
    lines.push({ type: 'added', text: line });
  }
  if (afterLines.length > MAX_FALLBACK_LINES_PER_SIDE) {
    lines.push({
      type: 'context',
      text: `${afterLines.length - MAX_FALLBACK_LINES_PER_SIDE} added lines hidden`,
    });
  }
  return lines;
}
